import React from "react";
import { useParams } from "react-router-dom";
import HubHeader from "../HubHeader";
import HubSidebar from "../HubSidebar";
import HubLoader from "../../../common/HubLoader";
import "../../../assets/css/Hub.css";
import { useState, useEffect, useRef } from "react";
import { ProgressSpinner } from 'primereact/progressspinner';
import { Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import MomentumAILogoSmall from "../../../assets/images/Momentum AI Icon-color (1).png";
import { getAMPConfig, getAllAMPs, AMPConfig } from "./ampConfigs";
import { IconBaseProps } from "react-icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Skeleton } from "primereact/skeleton";

type Message = {
  sender: "user" | "bot";
  text: string;
};

export default function AMPChatPage() {
  const { ampId, guid } = useParams<{ ampId: string; guid: string }>();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  // Store uploaded files as objects with fileName and fileURL
  const [uploadedFiles, setUploadedFiles] = useState<{ fileName: string, fileURL: string }[]>([]);
  const [uploadingFile, setUploadingFile] = useState<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [showFooterCard, setShowFooterCard] = useState(true);
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [ampConfig, setAmpConfig] = useState<AMPConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [allAmps, setAllAmps] = useState<AMPConfig[]>([]);
  const [showAmpPopover, setShowAmpPopover] = useState(false);
  const ampSelectorRef = useRef<HTMLDivElement | null>(null);
  const ampPopoverRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleCollapseToggle = (next: boolean) => setCollapsed(next);

  // Fetch all AMPs for the selector dropdown
  useEffect(() => {
    const loadAllAMPs = async () => {
      const amps = await getAllAMPs();
      setAllAmps(amps);
    };
    loadAllAMPs();
  }, []);

  // Fetch AMP configuration from API or static config
  useEffect(() => {
    const loadAMPConfig = async () => {
      if (!ampId) {
        navigate("/amps");
        return;
      }

      setLoading(true);
      const config = await getAMPConfig(ampId);
      // Add delay to show the loader
      await new Promise(resolve => setTimeout(resolve, 1500));

      if (!config) {
        // Invalid AMP ID, redirect to AMPs page
        navigate("/amps");
        return;
      }

      setAmpConfig(config);
      setLoading(false);
    };

    loadAMPConfig();
  }, [ampId, navigate]);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        showAmpPopover &&
        ampSelectorRef.current &&
        ampPopoverRef.current &&
        !ampSelectorRef.current.contains(target) &&
        !ampPopoverRef.current.contains(target)
      ) {
        setShowAmpPopover(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showAmpPopover]);

  // Reset messages when guid changes
  useEffect(() => {
    setMessages([]);
    setInput("");
    setShowFooterCard(true);
  }, [guid]);

  const generateGuid = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userText = input;

    setMessages((prev) => [...prev, { sender: "user", text: userText }]);
    scrollToBottom();
    setInput("");
    setShowFooterCard(false);

    let botIndex: number;

    setMessages((prev) => {
      botIndex = prev.length;
      return [...prev, { sender: "bot", text: "" }];
    });
    scrollToBottom();
    // If ampConfig.id === 'driver-license-scanner', call the special API with streaming (fetch)
    if (ampConfig && ampConfig.id === 'driver-license-scanner') {
      const formData = new FormData();
      formData.append('human_prompt', userText);
      formData.append('nc_token', localStorage.getItem('amsToken') || '');
      formData.append('conversationId', guid || '');
      formData.append('file_paths', JSON.stringify(uploadedFiles));
      formData.append('ai_service', 'DRIVING-LICENSE-CHAT');
      formData.append('rate_token', localStorage.getItem('rateToken') || '');
      formData.append('nowcerts_ams_url', 'https://newtest.nowcerts.com');

      try {
        const response = await fetch(
          'https://momentummyamp-stage.insurancegig.com/api/ams/driving-license-chat',
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${localStorage.getItem('authToken')}`,
              Accept: 'text/event-stream',
            },
            body: formData,
          }
        );

        if (response.status === 401) {
          localStorage.clear();
          navigate("/");
          return;
        }

        if (!response.body) {
          throw new Error("Streaming not supported");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = "";

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          const events = buffer.split("\n\n");
          buffer = events.pop() || "";

          for (const event of events) {
            if (!event.startsWith("data:")) continue;

            const payload = event.replace("data:", "").trim();
            if (!payload) continue;

            let parsed: any;
            try {
              parsed = JSON.parse(payload);
            } catch {
              continue;
            }

            if (parsed.type === "answer" && parsed.response) {
              setMessages((prev) => {
                const updated = [...prev];
                updated[botIndex] = {
                  ...updated[botIndex],
                  text: updated[botIndex].text + parsed.response,
                };
                return updated;
              });
              scrollToBottom();
            }

            if (parsed.responseStreaming === false) {
              setMessages((prev) => {
                const updated = [...prev];
                updated[botIndex] = {
                  ...updated[botIndex],
                };
                return updated;
              });

              reader.cancel();
              return;
            }
          }
        }
      } catch (error: any) {
        setMessages((prev) => [
          ...prev,
          { sender: "bot", text: "Something went wrong. Please try again." },
        ]);
      }
      return;
    }

    // Default: SUPPORT_ASSISTANT (existing logic)
    const formData = new FormData();
    formData.append("human_prompt", userText);
    formData.append("conversationId", guid || "");
    formData.append("nc_token", localStorage.getItem("amsToken") || "");
    formData.append("file_paths", JSON.stringify(uploadedFiles));
    formData.append("ai_service", "SUPPORT_ASSISTANT");
    formData.append("nowcerts_ams_url", "https://newtest.nowcerts.com");
    formData.append("rate_token", localStorage.getItem("rateToken") || "");

    try {
      const response = await fetch(
        "https://momentummyamp-stage.insurancegig.com/api/ams/chat",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            Accept: "text/event-stream",
          },
          body: formData,
        }
      );

      if (response.status === 401) {
        localStorage.clear();
        navigate("/");
        return;
      }

      if (!response.body) {
        throw new Error("Streaming not supported");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const events = buffer.split("\n\n");
        buffer = events.pop() || "";

        for (const event of events) {
          if (!event.startsWith("data:")) continue;

          const payload = event.replace("data:", "").trim();
          if (!payload) continue;

          let parsed: any;
          try {
            parsed = JSON.parse(payload);
          } catch {
            continue;
          }

          if (parsed.type === "answer" && parsed.response) {
            setMessages((prev) => {
              const updated = [...prev];
              updated[botIndex] = {
                ...updated[botIndex],
                text: updated[botIndex].text + parsed.response,
              };
              return updated;
            });
            scrollToBottom();
          }

          if (parsed.responseStreaming === false) {
            setMessages((prev) => {
              const updated = [...prev];
              updated[botIndex] = {
                ...updated[botIndex],
              };
              return updated;
            });

            reader.cancel();
            return;
          }
        }
      }
    } catch (error: any) {
      setMessages((prev) => [
        ...prev,
        { sender: "bot", text: "Something went wrong. Please try again." },
      ]);
    }
  };

  const scrollToBottom = (smooth = true) => {
    const el = messagesContainerRef.current;
    if (!el) return;

    requestAnimationFrame(() => {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: smooth ? "smooth" : "auto",
      });
    });
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && guid) {
      setUploadingFile(file.name);
      const formData = new FormData();
      formData.append('formFile', file);
      try {
        const response = await fetch(
          `https://datafabricapi-stage.insurancegig.com/api/momentummyamp/upload-file/${guid}`,
          {
            method: 'POST',
            body: formData,
          }
        );
        const data = await response.json();
        const fileUrl = data && data.fileUrl;
        const fileName = data && data.fileName;
        if (fileUrl && fileName) {
          setUploadedFiles(prev => [...prev, { fileName, fileURL: fileUrl }]);
        }
      } catch (error: any) {
        setMessages([
          ...messages,
          { sender: "user", text: `Uploaded file: ${file.name}` },
          { sender: "bot", text: `File upload failed. Please try again.` }
        ]);
      }
      setUploadingFile(null);
    }
    // Reset input value so same file can be selected again
    if (e.target) e.target.value = "";
  };

  const handleMicClick = () => {
    const recognition = new (window as any).webkitSpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setInput(transcript);
    };
    recognition.onerror = (event: any) => { };
    recognition.start();
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSend();
  };

  const handleAmpSelect = (selectedAmp: AMPConfig) => {
    const newGuid = generateGuid();
    navigate(`/amp/${selectedAmp.id}/${newGuid}`);
    setShowAmpPopover(false);
  };

  // Show loading state while fetching config
  if (loading || !ampConfig) {
    return (
      <div className="supportbot-landing-layout">
        <HubHeader />
        <div className="supportbot-landing-body">
          <aside className="supportbot-sidebar-wrap">
            <HubSidebar
              pagename="amp-chat"
              collapsed={collapsed}
              onCollapseToggle={handleCollapseToggle}
            />
          </aside>
          <div className="p-3 d-flex flex-column h-100">
            <main className="supportbot-main-content">
              <HubLoader message="Initializing AMP..." size="large" />
            </main>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="supportbot-landing-layout">
      <HubHeader />
      <div className="supportbot-landing-body">
        <aside
          className={`supportbot-sidebar-wrap ${collapsed ? "collapsed" : ""
            }`}
        >
          <HubSidebar
            pagename="amp-chat"
            collapsed={collapsed}
            onCollapseToggle={handleCollapseToggle}
          />
        </aside>
        <div className="p-3 d-flex flex-column h-100">
          <main className="supportbot-main-content">
            <main
              className={`amp-main-chat-container ${messages.length === 0 ? "amp-main-chat-container-empty" : ""
                }`}
              style={{ maxWidth: "1000px", width: "95%" }}
            >
              {messages.length === 0 ? (
                <div className="hub-empty-state">
                  <div className="hub-empty-icon" style={{
                    width: 95,
                    height: 95,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `linear-gradient(135deg, ${ampConfig.color}15 0%, ${ampConfig.color}08 100%)`,
                    borderRadius: '20px',
                    border: `2px solid ${ampConfig.color}20`
                  }}>
                    {React.createElement(ampConfig.icon as React.FC<IconBaseProps>, {
                      size: 48,
                      color: ampConfig.color
                    })}
                  </div>
                  <h2 className="hub-empty-title">
                    {ampConfig.emptyStateTitle || ampConfig.title}
                  </h2>
                  <p className="hub-empty-subtitle">
                    {ampConfig.emptyStateSubtitle || ampConfig.description}
                  </p>
                </div>
              ) : (
                <div className="hub-messages">
                  {messages.map((msg, idx) =>
                    msg.sender === "user" ? (
                      <div
                        key={idx}
                        className="hub-message hub-message-user"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          justifyContent: "flex-end",
                          background: "none",
                          boxShadow: "none",
                          padding: "12px 0",
                          marginBottom: "16px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <span
                            style={{
                              color: "#1f2937",
                              fontSize: "15px",
                              fontWeight: 500,
                              lineHeight: "1.4",
                            }}
                          >
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
                          </span>
                          <img
                            src="https://ui-avatars.com/api/?name=User&background=6b46c1&color=fff&size=24"
                            alt="User"
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: "50%",
                            }}
                          />
                        </div>
                      </div>
                    ) : (
                      <div
                        key={idx}
                        className="hub-message hub-message-bot"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          justifyContent: "flex-start",
                          background: "none",
                          boxShadow: "none",
                          padding: 0,
                        }}
                      >
                        <div style={{
                          width: 28,
                          height: 28,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: `linear-gradient(135deg, ${ampConfig.color}15 0%, ${ampConfig.color}08 100%)`,
                          borderRadius: '8px',
                          border: `1px solid ${ampConfig.color}20`,
                          flexShrink: 0
                        }}>
                          {React.createElement(ampConfig.icon as React.FC<IconBaseProps>, {
                            size: 16,
                            color: ampConfig.color
                          })}
                        </div>
                        {(!msg.text || msg.text.length === 0) ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            <Skeleton width="500px" height="1rem" />
                            <Skeleton width="300px" height="1rem" />
                            <Skeleton width="150px" height="1rem" />
                          </div>
                        ) : (
                          <span style={{ color: "#374151", fontWeight: 500, fontSize: "15px", lineHeight: "1.4" }}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
                          </span>
                        )}
                      </div>
                    )
                  )}
                  <div ref={chatEndRef} />
                </div>
              )}

              {/* Uploaded file card above chat input */}
              {(uploadingFile || uploadedFiles.length > 0) && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  marginBottom: 8,
                  gap: 8,
                  width: '100%'
                }}>
                  {uploadingFile ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      background: '#f3f3f3',
                      borderRadius: 8,
                      padding: '6px 14px',
                      fontSize: 14,
                      color: '#6b46c1',
                      minWidth: 120,
                      boxShadow: '0 1px 4px #0001',
                      gap: 8
                    }}>
                      <ProgressSpinner style={{ width: 18, height: 18 }} strokeWidth="4" />
                      <span style={{ fontWeight: 500 }}>{uploadingFile}</span>
                      <span style={{ fontSize: 12, color: '#888', marginLeft: 6 }}>(Uploading...)</span>
                    </div>
                  ) : uploadedFiles.length > 0 ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      background: '#f3f3f3',
                      borderRadius: 8,
                      padding: '6px 14px',
                      fontSize: 14,
                      color: '#6b46c1',
                      minWidth: 120,
                      boxShadow: '0 1px 4px #0001',
                      gap: 8
                    }}>
                      <span style={{ fontWeight: 500 }}>{uploadedFiles[uploadedFiles.length-1].fileName}</span>
                      <span style={{ fontSize: 12, color: '#888', marginLeft: 6 }}>(Uploaded)</span>
                    </div>
                  ) : null}
                </div>
              )}
              {/* ...existing code... */}
                <div className="hub-input-inner">
                  <div className="hub-input-left-section">
                    <div className="amp-selector-wrap" ref={ampSelectorRef}>
                      <button
                        type="button"
                        className="amp-selector-btn"
                        onClick={() => setShowAmpPopover((v) => !v)}
                        title="Switch AMP"
                      >
                        <span className="amp-selector-icon">
                          {React.createElement(ampConfig.icon as React.FC<IconBaseProps>, {
                            size: 14,
                            color: ampConfig.color
                          })}
                        </span>
                        <span className="amp-selector-label">
                          {ampConfig.title}
                        </span>
                        <span className="amp-selector-chevron">▾</span>
                      </button>
                      <div className="amp-selector-separator"></div>

                      {showAmpPopover && (
                        <div
                          className="amp-selector-popover"
                          ref={ampPopoverRef}
                        >
                          <div className="amp-options-grid">
                            {allAmps.map((amp) => {
                              const Icon = amp.icon as React.FC<IconBaseProps>;
                              return (
                                <button
                                  key={amp.id}
                                  type="button"
                                  className={`amp-option-card ${amp.id === ampConfig.id ? "active" : ""
                                    }`}
                                  onClick={() => handleAmpSelect(amp)}
                                >
                                  <div className="amp-option-icon">
                                    <Icon size={16} color={amp.color} />
                                  </div>
                                  <div className="amp-option-content">
                                    <span className="amp-option-title">
                                      {amp.title}
                                    </span>
                                    <p className="amp-option-desc">
                                      {amp.description}
                                    </p>
                                  </div>
                                  {amp.id === ampConfig.id && (
                                    <div className="amp-option-check">
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                        <polyline points="20,6 9,17 4,12" />
                                      </svg>
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    <button
                      className="hub-attach-btn hub-attach-purple"
                      onClick={handleUploadClick}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#6b46c1"
                        strokeWidth="2"
                      >
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                      </svg>
                      <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                        accept={ampConfig.acceptedFileTypes || "*"}
                      />
                    </button>
                  </div>
                  <input
                    type="text"
                    placeholder={ampConfig.placeholder || "What do you want to know?"}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleInputKeyDown}
                    className="hub-input"
                  />

                  <div className="hub-actions">
                    <button
                      onClick={handleSend}
                      disabled={!input.trim()}
                      className={`hub-send-btn ${input.trim() ? "hub-send-btn-active" : ""
                        }`}
                      title="Send"
                    >
                      <Send size={22} />
                    </button>
                    <button
                      className="hub-mic-btn"
                      title="Voice input"
                      onClick={handleMicClick}
                    >
                      <svg
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#6b46c1"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="9" y="2" width="6" height="12" rx="3" />
                        <path d="M5 10v2a7 7 0 0 0 14 0v-2" />
                        <line x1="12" y1="22" x2="12" y2="18" />
                      </svg>
                    </button>
                  </div>
                </div>

              <p className="hub-footer-text">
                By messaging Momentum, you agree to our{" "}
                <span className="hub-link">Terms</span> and{" "}
                <span className="hub-link">Privacy Policy</span>
              </p>
            </main>
          </main>
        </div>
      </div>
    </div>
  );
}
